import type { MeView } from '@research-assistant/shared';

import type { SupportTicketContext } from '@/lib/api/support.js';

/**
 * What the app knows about the user's current position, which the browser
 * cannot tell us. Everything else in the report is read from the environment.
 */
export interface BugReportContext {
  identity: MeView | null;
  workspaceId: string | null;
  conversationId: string | null;
  /** The chat answer the report is about, when it was sent from one. */
  messageId?: string | null;
  /** The error line that answer showed, when it failed. */
  errorText?: string | null;
}

/**
 * Browsers, most specific first. Chromium browsers all claim to be Chrome and
 * Safari, so Edge must be matched before Chrome and Chrome before Safari, or
 * every report would name the wrong one.
 */
const BROWSERS: Array<{ name: string; pattern: RegExp }> = [
  { name: 'Edge', pattern: /Edg(?:e|A|iOS)?\/([\d.]+)/ },
  { name: 'Chrome', pattern: /(?:Chrome|CriOS)\/([\d.]+)/ },
  { name: 'Firefox', pattern: /(?:Firefox|FxiOS)\/([\d.]+)/ },
  { name: 'Safari', pattern: /Version\/([\d.]+).*Safari/ },
];

/**
 * Operating systems, most specific first. Android's agent also says Linux, and
 * iOS says "like Mac OS X", so the mobile checks have to come first.
 */
const OPERATING_SYSTEMS: Array<{ name: string; pattern: RegExp }> = [
  { name: 'Android', pattern: /Android (\d+(?:[._]\d+)*)/ },
  { name: 'iOS', pattern: /(?:iPhone|iPad|iPod).*?OS (\d+(?:[._]\d+)*)/ },
  { name: 'Windows', pattern: /Windows NT/ },
  // The Mac version in the agent has been frozen at 10_15_7 since Catalina, so
  // reporting it would be a lie. Same for the Linux distro, which never appears.
  { name: 'macOS', pattern: /Mac OS X/ },
  { name: 'Linux', pattern: /Linux|X11/ },
];

/**
 * Trims a user-agent version down to what a human wants to read: Chrome's
 * `148.0.7778.96` becomes `148`, Safari's `17.4` stays `17.4`. Keeps at most
 * major.minor and then drops a trailing `.0` — the build and patch numbers only
 * matter to the vendor, and the raw agent is in the report if anyone needs them.
 */
function shortVersion(version: string): string {
  const parts = version.replace(/_/g, '.').split('.').slice(0, 2);
  while (parts.length > 1 && parts[parts.length - 1] === '0') {
    parts.pop();
  }
  return parts.join('.');
}

/**
 * Names the browser and OS behind a user-agent string. Deliberately coarse —
 * the raw agent goes into the report too, so this only has to be readable, and
 * an unrecognized agent says so rather than guessing.
 */
export function describeUserAgent(userAgent: string): {
  browser: string;
  os: string;
} {
  let browser = 'Unknown';
  for (const candidate of BROWSERS) {
    const match = candidate.pattern.exec(userAgent);
    if (match) {
      browser = `${candidate.name} ${shortVersion(match[1] ?? '')}`.trim();
      break;
    }
  }

  let os = 'Unknown';
  for (const candidate of OPERATING_SYSTEMS) {
    const match = candidate.pattern.exec(userAgent);
    if (match) {
      os = `${candidate.name} ${match[1] ? shortVersion(match[1]) : ''}`.trim();
      break;
    }
  }

  return { browser, os };
}

/**
 * The page URL with the query and fragment stripped.
 *
 * A URL's query and fragment can carry auth artifacts — MSAL's redirect lands
 * `#code=…&id_token=…` right back on the app — and this report gets pasted into a
 * Teams channel, so the whole `href` is not safe to copy. Origin and path are
 * what identify the page, and the ids that actually help a developer (workspace,
 * conversation) are reported from app state instead, so nothing useful is lost.
 * The note is there so a reader does not think the URL was bare.
 */
export function safePageUrl(location: {
  origin: string;
  pathname: string;
  search: string;
  hash: string;
}): string {
  const page = `${location.origin}${location.pathname}`;
  const hadMore = location.search !== '' || location.hash !== '';
  return hadMore ? `${page} (query and fragment omitted)` : page;
}

/** The IANA zone name, or '' where Intl cannot resolve one. */
function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return '';
  }
}

/**
 * Assembles the plain-text bug report the widget copies to the clipboard: where
 * the user is, what they are running it on, and which internal ids a developer
 * needs to find the request in the logs.
 *
 * Everything here is non-secret by construction — the report is user-visible in
 * the dialog and gets pasted into a Teams channel. It must never grow a field
 * that reads the platform JWT, a connector credential, or storage contents, and
 * see `safePageUrl` for why even the address bar is not copied whole.
 */
export function buildBugReport(context: BugReportContext): string {
  const { browser, os } = describeUserAgent(navigator.userAgent);
  const zone = timeZone();
  const lines = [
    'CTI bug report',
    // UTC and the zone are separate lines on purpose: a Z-suffixed stamp with a
    // zone name appended reads as local time in that zone, which it is not. UTC
    // is what the service logs are in; the zone is what the user's clock said.
    `Time (UTC): ${new Date().toISOString()}`,
  ];
  if (zone) lines.push(`Time zone: ${zone}`);
  lines.push(
    `URL: ${safePageUrl(window.location)}`,
    `Browser: ${browser}`,
    `OS: ${os}`,
    `Screen: ${window.screen.width}×${window.screen.height} (viewport ${window.innerWidth}×${window.innerHeight})`,
  );
  // Docker builds have no git to read a sha from, so vite bakes 'unknown' —
  // reporting that tells a reader nothing.
  if (__APP_VERSION__ && __APP_VERSION__ !== 'unknown') {
    lines.push(`App build: ${__APP_VERSION__}`);
  }

  if (context.identity) {
    lines.push(
      `User: ${context.identity.displayName} (${context.identity.userId})`,
    );
    lines.push(`Tenant: ${context.identity.tenantId}`);
  }
  if (context.workspaceId) lines.push(`Workspace: ${context.workspaceId}`);
  if (context.conversationId)
    lines.push(`Conversation: ${context.conversationId}`);
  if (context.messageId) lines.push(`Message: ${context.messageId}`);
  if (context.errorText) lines.push(`Error shown: ${context.errorText}`);

  lines.push(`User agent: ${navigator.userAgent}`);
  return lines.join('\n');
}

/**
 * The same facts as `buildBugReport`, as fields rather than prose, for the ticket the server files.
 *
 * It reads the same sources under the same rules — `safePageUrl` for the address bar, app state for
 * the ids, nothing from storage and nothing that could carry a credential. The clipboard text and
 * the ticket must never disagree about what was collected, so both are built from this one place.
 */
export function buildReportContext(
  context: BugReportContext,
): SupportTicketContext {
  const { browser, os } = describeUserAgent(navigator.userAgent);
  return {
    pageUrl: safePageUrl(window.location),
    browser,
    os,
    screen: `${window.screen.width}x${window.screen.height} (viewport ${window.innerWidth}x${window.innerHeight})`,
    timeZone: timeZone(),
    appBuild:
      __APP_VERSION__ && __APP_VERSION__ !== 'unknown'
        ? __APP_VERSION__
        : undefined,
    userAgent: navigator.userAgent,
    workspaceId: context.workspaceId,
    conversationId: context.conversationId,
    messageId: context.messageId,
    errorText: context.errorText,
  };
}
